/**
 * 약관 관리 목록 — 약관 종류 5종을 표시 순서대로 보여준다.
 *
 * 종류마다 현재 버전과 시행 예정 버전이 최대 1개씩 있다. 버전이 없는 종류는 둘 다 비어 있고,
 * 이때는 버전을 등록해야 앱에서 해당 약관이 노출된다.
 */
import { useTermsTypes } from '@/entities/terms/hooks/useTerms';
import type { TermsTypeListItem } from '@/entities/terms/api/terms.api';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { ROUTE_PATHS, termsDetailPath } from '@/shared/constants/routes';
import { formatDateTime } from '@/shared/lib/dateFormat';
import { handleApiError } from '@/shared/utils/toast';
import { useNavigate } from '@tanstack/react-router';
import { Pencil, Plus, ScrollText } from 'lucide-react';
import React, { useEffect } from 'react';

export const TermsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useTermsTypes();

  useEffect(() => {
    if (isError && error) handleApiError(error);
  }, [isError, error]);

  const rows = data ?? [];

  const goDetail = (termsId: number) => {
    navigate({ to: termsDetailPath(termsId) } as never);
  };

  const goCreate = (type: string) => {
    navigate({ to: ROUTE_PATHS.TERMS_NEW, search: { type } } as never);
  };

  /** 시행 예정 버전은 종류당 1개뿐이라, 새로 등록하는 대신 그 버전을 수정한다 */
  const goEditScheduled = (termsId: number) => {
    navigate({
      to: termsDetailPath(termsId),
      search: { mode: 'edit' },
    } as never);
  };

  /** 버전 셀 — 버전이 없으면 `-`, 있으면 눌러서 해당 버전 상세로 간다 */
  const renderVersionCell = (version: TermsTypeListItem['current']) => {
    if (!version) return <span className="text-muted-foreground">-</span>;
    return (
      <button
        type="button"
        className="text-left text-ruda-primary hover:underline"
        onClick={(e) => {
          e.stopPropagation();
          goDetail(version.termsId);
        }}
      >
        v{version.version}
        <span className="ml-2 text-muted-foreground">
          {formatDateTime(version.effectiveAt)}
        </span>
      </button>
    );
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={ScrollText}
        title="약관 관리"
        description="약관 종류별 현재 버전·시행 예정 버전 조회 및 버전 등록"
      />

      <DataCard>
        {isLoading ? (
          <LoadingSpinner />
        ) : rows.length === 0 ? (
          <EmptyState description="등록된 약관 종류가 없습니다." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>약관 종류</TableHead>
                <TableHead>동의 여부</TableHead>
                <TableHead>현재 버전 (시행일시)</TableHead>
                <TableHead>시행 예정 버전 (시행일시)</TableHead>
                <TableHead className="w-28">관리</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const target = row.current ?? row.scheduled;
                return (
                  <TableRow
                    key={row.type}
                    className={target ? 'cursor-pointer hover:bg-muted/50' : ''}
                    onClick={() => target && goDetail(target.termsId)}
                  >
                    <TableCell className="font-medium">
                      {row.typeName}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex flex-wrap items-center gap-1">
                        {row.agreementTarget ? (
                          <Badge variant={row.required ? 'default' : 'info'}>
                            {row.required ? '필수 동의' : '선택 동의'}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">게시 전용</Badge>
                        )}
                      </span>
                    </TableCell>
                    <TableCell>{renderVersionCell(row.current)}</TableCell>
                    <TableCell>{renderVersionCell(row.scheduled)}</TableCell>
                    <TableCell
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <MenuWriteGate>
                        {row.scheduled ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              goEditScheduled(row.scheduled!.termsId)
                            }
                          >
                            <Pencil className="mr-1 h-4 w-4" />
                            버전 수정
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => goCreate(row.type)}
                          >
                            <Plus className="mr-1 h-4 w-4" />
                            버전 등록
                          </Button>
                        )}
                      </MenuWriteGate>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DataCard>
    </div>
  );
};
